import { ProductContext } from '../../../types/ProductContext';
import { Button } from '../../cdl/Button/Button';
import { Text } from '../../cdl/Text/Text';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { IconPlus } from '../../common/icons/cdl/Plus';
import { Page } from '../../common/Page/Page';
import { PageGrid } from '../../common/PageGrid';
import { DebouncedSearchTextInput } from '../../common/Search/DebouncedSearchTextInput/DebouncedSearchTextInput';
import { Box } from '../../common/ui/Box';

import { OrderOverviewSortSelect } from './OrderOverviewSortSelect';
import { OrderOverviewTable } from './OrderOverviewTable';
import { CustomerOrderReportExportButton } from './components/CustomerOrderReportExportButton';
import { OverviewCustomerCompanySelect } from '../../common/form-elements/CompanySelect/OverviewCustomerCompanySelect';
import { OverviewSupplierCompanySelect } from '../../common/form-elements/CompanySelect/OverviewSupplierCompanySelect';
import { useNavigate, useSearch } from '@tanstack/react-router';

export const OrderOverviewPage = () => {
    const role = useRole();

    useDocumentTitle(translate('order.overviewPageTitle'));

    const navigate = useNavigate({ from: '/orders' });
    const search = useSearch({ from: '/_app/orders' });
    const canCreateEnquiry = role.isCustomer() && role.hasAnyCompanyWriteRights();

    return (
        <Page>
            <PageGrid>
                <Box display="flex" justifyContent="space-between" alignItems="center" columnGap={8}>
                    <Text variant="headline" as="h1">
                        {translate('order.overviewPageTitle')}
                    </Text>

                    <Box display="flex" columnGap={8}>
                        <CustomerOrderReportExportButton />

                        {canCreateEnquiry ? (
                            <Button
                                emphasis="high"
                                LeadingIcon={IconPlus}
                                onClick={() => navigate({ to: '/orders/new' })}
                            >
                                {translate('order.createEnquiryButton')}
                            </Button>
                        ) : null}
                    </Box>
                </Box>

                <Box display="flex" justifyContent="space-between" columnGap={8}>
                    <Box display="flex" columnGap={8}>
                        <Box minWidth="250px">
                            <DebouncedSearchTextInput
                                placeholder={translate('order.searchInputPlaceholder')}
                                value={search.searchQuery}
                                onChange={(searchQuery) => {
                                    navigate({
                                        search: (previousSearch) => ({
                                            ...previousSearch,
                                            searchQuery,
                                            page: 0,
                                        }),
                                    });
                                }}
                            />
                        </Box>

                        {!role.isOneCompanyUser() || role.isSuperAdmin() ? (
                            <OverviewCustomerCompanySelect
                                value={search.customerIds}
                                onChange={(customerIds) => {
                                    navigate({
                                        search: (previousSearch) => ({
                                            ...previousSearch,
                                            customerIds,
                                            page: 0,
                                        }),
                                    });
                                }}
                            />
                        ) : null}

                        {role.isSuperAdmin() ? (
                            <OverviewSupplierCompanySelect
                                value={search.supplierIds}
                                onChange={(supplierIds) => {
                                    navigate({
                                        search: (previousSearch) => ({
                                            ...previousSearch,
                                            supplierIds,
                                            page: 0,
                                        }),
                                    });
                                }}
                            />
                        ) : null}
                    </Box>

                    <Box minWidth="250px">
                        <OrderOverviewSortSelect
                            onChange={(sortValue) => {
                                navigate({
                                    search: (previousSearch) => ({
                                        ...previousSearch,
                                        sortValue,
                                    }),
                                });
                            }}
                            value={search.sortValue}
                        />
                    </Box>
                </Box>

                <OrderOverviewTable />
            </PageGrid>
        </Page>
    );
};
